/**
 * CR413: Structured Eligibility Requirements V1
 * 
 * Type definition, fallback derivation, and bullet-point rendering
 * for the Deal Card back-face Eligibility section.
 * 
 * Gated by: ff_eligibility_requirements_v1
 */

import type { Deal } from "@/lib/types/deals";
import { formatEligibilityRange } from "@/lib/deals/formatEligibilityRange";
import { getDealValidity } from "@/lib/deals/dealValidity";

// =============================================================================
// TYPES
// =============================================================================

export type EligibilityRequirementsV1 = {
  version: "v1";

  where?: { merchant: string };

  buy?: {
    type: "gift_card";
    brand: string;
    amount_cad?: number;
  };

  when?: {
    valid_from_date?: string;   // date-only "YYYY-MM-DD"
    valid_until_date?: string;  // date-only "YYYY-MM-DD"
    notes?: string;             // e.g. "Valid dates unknown"
  };

  get?: {
    type: "discount" | "bonus" | "points" | "in_kind";
    amount_cad?: number;
    target?: string;            // e.g. "flowers"
    text?: string;              // fallback display text
  };

  also?: Array<{
    type: "min_spend" | "limit" | "membership" | "exclusions" | "other";
    amount_cad?: number;
    target?: string;
    text: string;               // always required for display
  }>;
};

// =============================================================================
// VALIDATION
// =============================================================================

/**
 * Type-guard: checks that a value is a valid EligibilityRequirementsV1.
 * Intentionally lenient on optional keys — only enforces version === "v1"
 * and top-level shape being an object.
 */
export function isValidEligibilityRequirementsV1(
  val: unknown
): val is EligibilityRequirementsV1 {
  if (!val || typeof val !== "object" || Array.isArray(val)) return false;
  return (val as Record<string, unknown>).version === "v1";
}

// =============================================================================
// DERIVATION HELPER
// =============================================================================

/**
 * Best-effort derivation of EligibilityRequirementsV1 from existing canonical
 * deal fields. Used as fallback when eligibility_requirements is null/malformed.
 * 
 * MUST NOT THROW.
 */
export function deriveEligibilityRequirementsV1(deal: Deal): EligibilityRequirementsV1 {
  const req: EligibilityRequirementsV1 = { version: "v1" };

  try {
    // -- WHERE --
    const merchant = deal.promo_merchant_name || deal.merchant || null;
    if (merchant) {
      req.where = { merchant };
    }

    // -- BUY --
    const brand = deal.qualifying_product_name
      || deal.reward_brand_name
      || deal.purchase_gift_card_brand
      || deal.gift_card_brand
      || null;
    if (brand) {
      req.buy = {
        type: "gift_card",
        brand,
        amount_cad: deal.qualifying_spend_cad ?? deal.spend_amount ?? undefined,
      };
    }

    // -- WHEN -- CR432: Use unified validity helper
    const validity = getDealValidity(deal);
    const fromDate = validity.validFrom;
    const untilDate = validity.validTo;

    if (fromDate || untilDate) {
      req.when = {
        valid_from_date: fromDate ?? undefined,
        valid_until_date: untilDate ?? undefined,
      };
    } else {
      // No dates available
      req.when = { notes: "Valid dates unknown" };
    }

    // -- GET --
    const dealCategory = deal.deal_category || deal.deal_type?.toUpperCase();
    if (dealCategory) {
      const getType = mapGetType(dealCategory);
      if (getType) {
        const amount = deal.bonus_value_cad ?? deal.bonus_amount ?? undefined;
        req.get = {
          type: getType,
          amount_cad: amount ?? undefined,
          text: deal.headline_mechanic ?? undefined,
        };
      }
    }

    // -- ALSO (v1: empty by default, fine-print parsing is Phase 3) --
    // We don't populate also[] from free-text fine print in v1.
    // Only explicit canonical fields would go here.
  } catch {
    // MUST NOT THROW — return whatever we've built so far
  }

  return req;
}

function mapGetType(category: string): EligibilityRequirementsV1["get"] extends { type: infer T } ? T : never | null {
  switch (category) {
    case "BONUS": return "bonus";
    case "DISCOUNT": return "discount";
    case "POINTS": return "points";
    case "IN_KIND": return "in_kind";
    default: return null as never;
  }
}

// =============================================================================
// BULLET RENDERING
// =============================================================================

/**
 * Convert EligibilityRequirementsV1 into an ordered array of bullet strings
 * suitable for rendering in the card back-face.
 * 
 * Order: Where → Buy → When → Get → Also
 * Skips null/empty items.
 */
export function eligibilityToBullets(req: EligibilityRequirementsV1): string[] {
  const bullets: string[] = [];

  // WHERE
  if (req.where?.merchant) {
    bullets.push(`Where: ${req.where.merchant}`);
  }

  // BUY
  if (req.buy) {
    const parts = ["Buy:"];
    if (req.buy.amount_cad != null) {
      parts.push(`Buy a $${req.buy.amount_cad}`);
      parts[0] = ""; // replace "Buy:" prefix
    }
    if (req.buy.brand) {
      if (req.buy.amount_cad != null) {
        parts.push(`${req.buy.brand} gift card`);
      } else {
        parts.push(`Buy a ${req.buy.brand} gift card`);
        parts[0] = "";
      }
    }
    const text = parts.filter(Boolean).join(" ").trim();
    if (text) bullets.push(text);
  }

  // WHEN
  if (req.when) {
    if (req.when.valid_from_date || req.when.valid_until_date) {
      const formatted = formatEligibilityRange(
        req.when.valid_from_date,
        req.when.valid_until_date
      );
      if (formatted) {
        bullets.push(`When: ${formatted}`);
      }
    } else if (req.when.notes) {
      bullets.push(`When: ${req.when.notes}`);
    }
  }

  // GET
  if (req.get) {
    let getText: string | null = null;
    if (req.get.amount_cad != null && req.get.target) {
      getText = `$${req.get.amount_cad} off ${req.get.target}`;
    } else if (req.get.amount_cad != null) {
      getText = `$${req.get.amount_cad} ${req.get.type === "bonus" ? "bonus" : "off"}`;
    } else if (req.get.text) {
      getText = req.get.text;
    }
    if (getText) {
      bullets.push(`Get: ${getText}`);
    }
  }

  // ALSO
  if (req.also && req.also.length > 0) {
    for (const item of req.also) {
      if (item.text) {
        bullets.push(`Also: ${item.text}`);
      }
    }
  }

  return bullets;
}
